import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TableCell,
  Chip,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Build as BuildIcon
} from '@mui/icons-material';
import TrackingBase from '../../components/TrackingBase/TrackingBase';
import LocationViewer from '../../components/LocationViewer/LocationViewer';
import { equipmentAPI, devicesAPI, suppliersAPI, barriersAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';

const AssetTracking = () => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [devices, setDevices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [barriers, setBarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationDialog, setLocationDialog] = useState({ open: false, asset: null });
  const [filters, setFilters] = useState({ name: '', status: '', location: '' });
  


  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await equipmentAPI.getWithLocation();
      console.log('=== ASSET TRACKING API RESPONSE ===', response);
      const equipmentData = response.data?.data || [];
      console.log('Equipment data:', equipmentData);
      if (equipmentData.length > 0) {
        console.log('First equipment sample:', equipmentData[0]);
      }
      
      const assetsData = equipmentData.map(eq => {
        console.log('Mapping equipment:', eq);
        
        return {
          id: eq.id,
          equipmentName: eq.equipment_name,
          equipmentType: eq.equipment_type,
          category: eq.category,
          ownership: eq.ownership,
          supplier_id: eq.supplier_id,
          supplier_name: eq.supplier_name || 'N/A',
          device_id: eq.device_id,
          validation_date: eq.validation_date,
          expiry_date: eq.expiry_date,
          responsible_person: eq.responsible_person,
          department: eq.department,
          associated_barriers: eq.associated_barriers || [],
          alert_type: eq.alert_type || 'always',
          notes: eq.notes,
          ble_id: eq.ble_id,
          x: parseFloat(eq.location_x || 0),
          y: parseFloat(eq.location_y || 0),
          status: eq.status || 'offline',
          last_location_update: eq.location_timestamp || eq.updated_at,
          battery_level: eq.battery_level,
          accuracy: eq.accuracy,
          location: eq.area_name || 'Não definida',
          // Dados adicionais para compatibilidade
          warranty_expiry: eq.expiry_date,
          maintenance_due: eq.next_maintenance_date,
          alert_types: eq.alert_settings || [],
          barrier_alerts: eq.barrier_alerts_enabled || false
        };
      });
      
      setAssets(assetsData);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
    loadDevices();
    loadSuppliers();
    loadBarriers();
  }, [fetchAssets]);

  const loadBarriers = async () => {
    try {
      const response = await barriersAPI.getAll();
      const barriersData = Array.isArray(response?.data?.data) ? response.data.data : 
                          Array.isArray(response?.data) ? response.data : [];
      setBarriers(barriersData);
    } catch (error) {
      console.warn('Failed to load barriers:', error.message);
      setBarriers([]);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await devicesAPI.getAll();
      const devicesData = Array.isArray(response?.data?.data) ? response.data.data : 
                         Array.isArray(response?.data) ? response.data : [];
      const trackableDevices = devicesData.filter(device => 
        ['beacon', 'wristband', 'badge', 'rfid_tag'].includes(device.type) && 
        (device.ble_id || device.epc)
      );
      setDevices(trackableDevices);
    } catch (error) {
      console.warn('Failed to load devices:', error.message);
      setDevices([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await suppliersAPI.getAll();
      setSuppliers(response.data?.data || []);
    } catch (error) {
      console.warn('Failed to load suppliers:', error.message);
      setSuppliers([]);
    }
  };

  // Handlers para o componente base
  const handleCreateAsset = async (formData) => {
    return await equipmentAPI.create(formData);
  };

  const handleEditAsset = async (assetId, formData) => {
    return await equipmentAPI.update(assetId, formData);
  };

  const handleDeleteAsset = async (assetId) => {
    return await equipmentAPI.delete(assetId);
  };

  const handleAssociateBeacon = async (assetId, beaconData) => {
    // Get current asset data to preserve all fields
    const currentAsset = assets.find(a => a.id === assetId);
    if (!currentAsset) {
      throw new Error('Equipment not found');
    }
    
    // Send all current data plus the new device_id
    const updateData = {
      equipment_name: currentAsset.equipmentName,
      equipment_type: currentAsset.equipmentType,
      category: currentAsset.category,
      ownership: currentAsset.ownership,
      supplier_id: currentAsset.supplier_id || '',
      device_id: beaconData.device_id,
      validation_date: currentAsset.validation_date || '',
      expiry_date: currentAsset.expiry_date || '',
      responsible_person: currentAsset.responsible_person || '',
      department: currentAsset.department || '',
      associated_barriers: currentAsset.associated_barriers || [],
      alert_type: currentAsset.alert_type || 'always',
      notes: currentAsset.notes || ''
    };
    
    return await equipmentAPI.update(assetId, updateData);
  };

  const handleViewLocation = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    if (asset) {
      setLocationDialog({ open: true, asset });
    }
  };

  const handleCloseLocationDialog = () => {
    setLocationDialog({ open: false, asset: null });
  };

  // Utilitários
  const getStatusColor = (timestamp) => {
    if (!timestamp) return 'default';
    const now = new Date();
    const lastUpdate = new Date(timestamp);
    const diffMinutes = (now - lastUpdate) / (1000 * 60);
    
    if (diffMinutes < 5) return 'success';
    if (diffMinutes < 15) return 'warning';
    return 'error';
  };

  const getStatusText = (timestamp) => {
    if (!timestamp) return 'Sem localização';
    const now = new Date();
    const lastUpdate = new Date(timestamp);
    const diffMinutes = Math.floor((now - lastUpdate) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Agora';
    if (diffMinutes < 60) return `${diffMinutes}min atrás`;
    return `${Math.floor(diffMinutes / 60)}h atrás`;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'medical': 'primary',
      'surgical': 'success',
      'diagnostic': 'info',
      'support': 'warning',
      'emergency': 'error',
      'other': 'default'
    };
    return colors[category] || 'default';
  };

  // Métricas para o dashboard
  const getMetrics = (assets) => ({
    total: {
      label: 'Total de Equipamentos',
      value: assets.length,
    },
    active: {
      label: 'Ativos Agora',
      value: assets.filter(a => getStatusColor(a.last_location_update) === 'success').length,
      color: 'success.main',
    },
    tracked: {
      label: 'Com Rastreamento',
      value: assets.filter(a => a.device_id).length,
      color: 'primary.main',
    },
    offline: {
      label: 'Offline',
      value: assets.filter(a => a.status === 'offline').length,
      color: 'error.main',
    },
  });

  // Renderizadores de formulário
  const renderCreateEditForm = (selectedItem, tabType, onSubmit) => {
    const handleSubmit = (e) => {
      e.preventDefault();
      const formDataToSubmit = new FormData(e.target);
      const data = {
        equipment_name: formDataToSubmit.get('equipment_name') || '',
        equipment_type: formDataToSubmit.get('equipment_type') || '',
        category: formDataToSubmit.get('category') || 'normal',
        ownership: formDataToSubmit.get('ownership') || 'owned',
        supplier_id: formDataToSubmit.get('supplier_id') || '',
        device_id: selectedItem?.device_id || '',
        validation_date: formDataToSubmit.get('validation_date') || '',
        expiry_date: formDataToSubmit.get('expiry_date') || '',
        responsible_person: formDataToSubmit.get('responsible_person') || '',
        department: formDataToSubmit.get('department') || '',
        associated_barriers: formDataToSubmit.getAll('associated_barriers') || [],
        alert_type: formDataToSubmit.get('alert_type') || 'always',
        notes: formDataToSubmit.get('notes') || ''
      };
      console.log('Form data being submitted:', data);
      onSubmit(data);
    };

    if (tabType === 'basic') {
      return (
        <form id="tracking-form" onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="equipment_name"
                label="Nome do Equipamento"
                fullWidth
                required
                defaultValue={selectedItem?.equipmentName || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="equipment_type"
                label="Tipo"
                fullWidth
                defaultValue={selectedItem?.equipmentType || ''}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Categoria</InputLabel>
                <Select
                  name="category"
                  defaultValue={selectedItem?.category || 'normal'}
                  label="Categoria"
                >
                  <MenuItem value="critical">Crítico</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Baixo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Propriedade</InputLabel>
                <Select
                  name="ownership"
                  defaultValue={selectedItem?.ownership || 'owned'}
                  label="Propriedade"
                >
                  <MenuItem value="owned">Próprio</MenuItem>
                  <MenuItem value="rented">Alugado</MenuItem>
                  <MenuItem value="borrowed">Emprestado</MenuItem>
                  <MenuItem value="consignment">Consignado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Fornecedor</InputLabel>
                <Select
                  name="supplier_id"
                  defaultValue={selectedItem?.supplier_id || ''}
                  label="Fornecedor"
                >
                  <MenuItem value="">
                    <em>Selecione um fornecedor</em>
                  </MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="validation_date"
                label="Data de Validação"
                type="date"
                fullWidth
                required
                defaultValue={selectedItem?.validation_date || ''}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="expiry_date"
                label="Data de Vencimento"
                type="date"
                fullWidth
                required
                defaultValue={selectedItem?.expiry_date || selectedItem?.warranty_expiry || ''}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="responsible_person"
                label="Responsável"
                fullWidth
                defaultValue={selectedItem?.responsible_person || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="department"
                label="Departamento"
                fullWidth
                defaultValue={selectedItem?.department || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Alerta</InputLabel>
                <Select
                  name="alert_type"
                  defaultValue={selectedItem?.alert_type || 'always'}
                  label="Tipo de Alerta"
                >
                  <MenuItem value="always">Sempre ao passar</MenuItem>
                  <MenuItem value="expired_only">Apenas quando vencido</MenuItem>
                  <MenuItem value="expiring_soon">Vencendo em 30 dias</MenuItem>
                  <MenuItem value="never">Nunca alertar</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Barreiras Associadas</InputLabel>
                <Select
                  name="associated_barriers"
                  multiple
                  defaultValue={selectedItem?.associated_barriers || []}
                  label="Barreiras Associadas"
                >
                  {barriers.map((barrier) => (
                    <MenuItem key={barrier.id} value={barrier.id}>
                      {barrier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Observações"
                fullWidth
                multiline
                rows={2}
                defaultValue={selectedItem?.notes || ''}
              />
            </Grid>
          </Grid>
        </form>
      );
    }

    if (tabType === 'rtls') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Configurações de Rastreamento
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="barrier_alerts_enabled"
                  defaultChecked={Boolean(selectedItem?.barrier_alerts)}
                />
              }
              label="Alertas de Barreira"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="alert_types"
              label="Tipos de Alertas (separados por vírgula)"
              fullWidth
              defaultValue={Array.isArray(selectedItem?.alert_types) ? selectedItem.alert_types.join(', ') : ''}
              placeholder="movimento, temperatura, bateria"
            />
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const renderBeaconForm = (selectedItem, availableDevices, onSubmit) => {
    const handleSubmit = (e) => {
      e.preventDefault();
      const formDataToSubmit = new FormData(e.target);
      const data = {
        device_id: formDataToSubmit.get('device_id') || ''
      };
      onSubmit(data);
    };

    return (
      <form id="tracking-form" onSubmit={handleSubmit}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Dispositivo Rastreável Associado</InputLabel>
              <Select
                name="device_id"
                defaultValue={String(selectedItem?.device_id || '')}
                label="Dispositivo Rastreável Associado"
              >
                <MenuItem value="">
                  <em>Nenhum dispositivo</em>
                </MenuItem>
                {Array.isArray(availableDevices) && availableDevices.map((device) => (
                  <MenuItem key={device.id} value={String(device.id)}>
                    <Box>
                      <Typography variant="body2">
                        [{device.type.toUpperCase()}] {device.ble_id || device.epc} - {device.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Bateria: {device.battery_level || 'N/A'}% | Status: {device.status || 'offline'}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </form>
    );
  };

  // Renderizadores para filtros
  const renderFilters = (filters, onFiltersChange) => (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={4}>
        <TextField
          label="Filtrar por Nome"
          value={filters.name}
          onChange={(e) => onFiltersChange({ ...filters, name: e.target.value })}
          fullWidth
          size="small"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Filtrar por Localização"
          value={filters.location}
          onChange={(e) => onFiltersChange({ ...filters, location: e.target.value })}
          fullWidth
          size="small"
        />
      </Grid>
    </Grid>
  );

  // Renderizadores para o componente base
  const renderTableColumns = () => [
    <TableCell key="equipment">Equipamento</TableCell>,
    <TableCell key="category">Categoria</TableCell>,
    <TableCell key="ownership">Propriedade</TableCell>,
    <TableCell key="supplier">Fornecedor</TableCell>,
    <TableCell key="expiry">Vencimento</TableCell>,
    <TableCell key="responsible">Responsável</TableCell>,
    <TableCell key="department">Departamento</TableCell>,
    <TableCell key="barriers">Barreiras</TableCell>,
    <TableCell key="alert_type">Tipo Alerta</TableCell>,
    <TableCell key="location">Localização</TableCell>,
    <TableCell key="device">Dispositivo</TableCell>,
    <TableCell key="status">Status</TableCell>,
  ];

  const getOwnershipColor = (ownership) => {
    const colors = {
      'hospital': 'primary',
      'leased': 'warning',
      'rented': 'info',
      'third_party': 'secondary'
    };
    return colors[ownership] || 'default';
  };

  const getOwnershipLabel = (ownership) => {
    const labels = {
      'hospital': 'Hospital',
      'leased': 'Locado',
      'rented': 'Alugado',
      'third_party': 'Terceiros'
    };
    return labels[ownership] || ownership;
  };

  const getWarrantyStatus = (warrantyDate) => {
    if (!warrantyDate) return { color: 'default', text: 'N/A' };
    
    const now = new Date();
    const warranty = new Date(warrantyDate);
    const diffDays = Math.ceil((warranty - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { color: 'error', text: 'Vencida' };
    if (diffDays < 30) return { color: 'warning', text: `${diffDays}d` };
    if (diffDays < 90) return { color: 'info', text: `${diffDays}d` };
    return { color: 'success', text: `${diffDays}d` };
  };

  const renderTableRow = (asset) => [
    <TableCell key="equipment">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BuildIcon color="action" />
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {asset.equipmentName}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {asset.equipmentType}
          </Typography>
        </Box>
      </Box>
    </TableCell>,
    <TableCell key="category">
      <Chip
        label={asset.category === 'critical' ? 'Crítico' : asset.category === 'normal' ? 'Normal' : asset.category === 'low' ? 'Baixo' : asset.category}
        color={asset.category === 'critical' ? 'error' : asset.category === 'normal' ? 'primary' : 'default'}
        size="small"
      />
    </TableCell>,
    <TableCell key="ownership">
      <Chip
        label={asset.ownership === 'owned' ? 'Próprio' : asset.ownership === 'rented' ? 'Alugado' : asset.ownership === 'borrowed' ? 'Emprestado' : asset.ownership === 'consignment' ? 'Consignado' : asset.ownership}
        color={asset.ownership === 'owned' ? 'success' : asset.ownership === 'rented' ? 'warning' : asset.ownership === 'borrowed' ? 'info' : 'secondary'}
        size="small"
        variant="outlined"
      />
    </TableCell>,
    <TableCell key="supplier">
      <Typography variant="body2">
        {asset.supplier_name && asset.supplier_name !== 'N/A' ? asset.supplier_name : 'N/A'}
      </Typography>
    </TableCell>,
    <TableCell key="expiry">
      <Typography variant="body2">
        {asset.expiry_date ? new Date(asset.expiry_date).toLocaleDateString('pt-BR') : '-'}
      </Typography>
    </TableCell>,
    <TableCell key="responsible">
      <Typography variant="body2">
        {asset.responsible_person || '-'}
      </Typography>
    </TableCell>,
    <TableCell key="department">
      <Typography variant="body2">
        {asset.department || '-'}
      </Typography>
    </TableCell>,
    <TableCell key="barriers">
      {Array.isArray(asset.associated_barriers) && asset.associated_barriers.length > 0 ? (
        asset.associated_barriers.map(barrierId => {
          const barrier = barriers.find(b => b.id === barrierId);
          return barrier ? (
            <Chip
              key={barrierId}
              label={barrier.name}
              size="small"
              sx={{ mr: 0.5, mb: 0.5 }}
              color="primary"
              variant="outlined"
            />
          ) : null;
        })
      ) : (
        <Typography variant="caption" color="textSecondary">
          Nenhuma
        </Typography>
      )}
    </TableCell>,
    <TableCell key="alert_type">
      <Chip
        label={
          asset.alert_type === 'always' ? 'Sempre' :
          asset.alert_type === 'expired_only' ? 'Vencido' :
          asset.alert_type === 'expiring_soon' ? 'Vencendo' : 'Nunca'
        }
        size="small"
        color={
          asset.alert_type === 'always' ? 'primary' :
          asset.alert_type === 'expired_only' ? 'error' :
          asset.alert_type === 'expiring_soon' ? 'warning' : 'default'
        }
      />
    </TableCell>,
    <TableCell key="location">
      {asset.x && asset.y ? (
        <Box>
          <Typography variant="body2">
            X: {asset.x.toFixed(2)}, Y: {asset.y.toFixed(2)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {asset.location}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {asset.location || 'Sem localização'}
        </Typography>
      )}
    </TableCell>,
    <TableCell key="device">
      {asset.device_id ? (
        <Box>
          <Typography variant="body2" fontFamily="monospace">
            {asset.ble_id || asset.device_id}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Bateria: {asset.battery_level || 'N/A'}%
          </Typography>
        </Box>
      ) : (
        <Chip label="Sem dispositivo" color="default" size="small" />
      )}
    </TableCell>,
    <TableCell key="status">
      <Box>
        <Chip
          label={asset.status || 'offline'}
          color={asset.status === 'online' ? 'success' : 'error'}
          size="small"
        />
        <Typography variant="caption" display="block" color="textSecondary">
          {getStatusText(asset.last_location_update)}
        </Typography>
      </Box>
    </TableCell>,
  ];

  return (
    <>
      <TrackingBase
        title="Rastreamento de Equipamentos"
        data={assets}
        loading={loading}
        onRefresh={fetchAssets}
        onCreateItem={handleCreateAsset}
        onEditItem={handleEditAsset}
        onDeleteItem={handleDeleteAsset}
        onAssociateBeacon={handleAssociateBeacon}
        onViewLocation={handleViewLocation}
        renderTableColumns={renderTableColumns}
        renderTableRow={renderTableRow}
        getMetrics={getMetrics}
        availableDevices={devices}
        filters={filters}
        onFiltersChange={setFilters}
        renderFilters={renderFilters}
        renderCreateEditForm={renderCreateEditForm}
        renderBeaconForm={renderBeaconForm}
      />
      
      <LocationViewer
        open={locationDialog.open}
        onClose={handleCloseLocationDialog}
        entity={locationDialog.asset}
        entityType="equipment"
      />
    </>
  );
};

export default AssetTracking;